import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { ChevronDown, ChevronUp } from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { CopyButton } from "@/components/CopyButton";
import { api, validatePlate, normalizePlate } from "@/lib/api";
import { VehicleAutocomplete, COMMON_PROFESSIONS, COMMON_BRANDS, COMMON_MODELS_BY_BRAND } from "@/components/VehicleAutocomplete";
import { MoneyInput } from "@/components/MoneyInput";
import { PlateInput } from "@/components/PlateInput";
import { DatePicker } from "@/components/DatePicker";

/**
 * Dynamic quote details form, fields depend on policy type.
 * Props: type (string), details (object), onChange(newDetails)
 */
export const QuoteDetailsForm = ({ type, details, onChange, onAddFile, requirePlate = false, showDrivingTypeWarning = false, publicMode = false, publicKinshipDegrees = null, publicAnnualMileage = null, publicAntitheft = null, publicEducation = null, publicCurrentCompanies = null, publicClientKind = "person" }) => {
  const d = details || {};
  const set = (patch) => onChange({ ...d, ...patch });
  // 🆕 Sezioni a scomparsa (default: espanse). Comuni a tutte le tipologie polizza.
  const [vehicleCollapsed, setVehicleCollapsed] = useState(false);
  const [driverCollapsed, setDriverCollapsed] = useState(false);
  const [historyCollapsed, setHistoryCollapsed] = useState(false);
  const [professions, setProfessions] = useState(COMMON_PROFESSIONS);
  const [companyNames, setCompanyNames] = useState([]);
  const [dbBrands, setDbBrands] = useState([]);
  const [dbModels, setDbModels] = useState([]);
  const [kinshipDegrees, setKinshipDegrees] = useState(Array.isArray(publicKinshipDegrees) ? publicKinshipDegrees : []);
  // 🆕 Config tipologia polizza corrente (per veicolo dinamico + prodotti/garanzie)
  const [policyTypeConfig, setPolicyTypeConfig] = useState(null);
  const [policyProducts, setPolicyProducts] = useState([]);
  useEffect(() => {
    api.get("/utils/professions")
      .then(({ data }) => setProfessions([...COMMON_PROFESSIONS, ...(data.professions || [])]))
      .catch(() => {});
    api.get("/utils/companies-list")
      .then(({ data }) => setCompanyNames(data.companies || []))
      .catch(() => {});
    api.get("/utils/vehicle-brands")
      .then(({ data }) => setDbBrands(data.brands || []))
      .catch(() => {});
    if (!publicMode) {
      api.get("/kinship-degrees")
        .then(({ data }) => setKinshipDegrees(Array.isArray(data) ? data.filter((k) => k.is_active !== false) : []))
        .catch(() => {});
    } else if (Array.isArray(publicKinshipDegrees)) {
      setKinshipDegrees(publicKinshipDegrees);
    }
  }, [publicMode, publicKinshipDegrees]);
  // 🆕 Effetto separato sul cambio tipo: carica la config tipologia + prodotti
  useEffect(() => {
    if (!type || publicMode) {
      setPolicyTypeConfig(null);
      setPolicyProducts([]);
      return;
    }
    api.get("/policy-types")
      .then(({ data }) => {
        const cfg = (data || []).find((t) => t.code === type);
        setPolicyTypeConfig(cfg || null);
      })
      .catch(() => setPolicyTypeConfig(null));
    api.get(`/policy-products/${type}`)
      .then(({ data }) => setPolicyProducts(Array.isArray(data) ? data.filter((p) => p.is_active !== false) : []))
      .catch(() => setPolicyProducts([]));
  }, [type, publicMode]);

  // Carica modelli filtrati per marca quando cambia
  const currentBrand = (d.vehicle_brand || "").trim();
  useEffect(() => {
    if (!currentBrand) { setDbModels([]); return; }
    api.get("/utils/vehicle-models", { params: { brand: currentBrand } })
      .then(({ data }) => setDbModels(data.models || []))
      .catch(() => setDbModels([]));
  }, [currentBrand]);

  const brandSuggestions = useMemo(() => [...new Set([...COMMON_BRANDS, ...dbBrands])], [dbBrands]);
  const modelSuggestions = useMemo(() => {
    const key = (currentBrand || "").toLowerCase();
    const fromDataset = COMMON_MODELS_BY_BRAND[key] || [];
    return [...new Set([...fromDataset, ...dbModels])];
  }, [currentBrand, dbModels]);

  // Auto-calcolo "tipo di guida" dall'età stimata (data primo rilascio patente o conducente più giovane).
  // Regola: età 18-26 → "libera"; >26 → "esperta" (di default). Non sovrascrive se l'utente ha già scelto.
  const licenseIssue = d.license_first_issue;
  const youngestAgeStr = d.youngest_driver_age;
  const drivingType = d.driving_type;
  const drivingTypeManual = d.driving_type_manual;
  useEffect(() => {
    if (drivingTypeManual) return;
    let ageFromLicense = null;
    if (licenseIssue) {
      const dt = new Date(licenseIssue);
      if (!Number.isNaN(dt.getTime())) {
        const now = new Date();
        let years = now.getFullYear() - dt.getFullYear();
        const m = now.getMonth() - dt.getMonth();
        if (m < 0 || (m === 0 && now.getDate() < dt.getDate())) years -= 1;
        ageFromLicense = Math.max(18, 18 + years);
      }
    }
    const youngest = youngestAgeStr ? parseInt(youngestAgeStr, 10) : null;
    const candidates = [ageFromLicense, Number.isFinite(youngest) ? youngest : null].filter((x) => Number.isFinite(x));
    if (candidates.length === 0) return;
    const minAge = Math.min(...candidates);
    const suggested = minAge >= 18 && minAge <= 26 ? "libera" : "esperta";
    if (drivingType !== suggested) {
      onChange({ ...d, driving_type: suggested });
    }
  }, [licenseIssue, youngestAgeStr, drivingTypeManual]);
  const toggleCoverage = (key) => {
    const list = new Set(d.coverage_options || []);
    if (list.has(key)) list.delete(key); else list.add(key);
    set({ coverage_options: [...list] });
  };

  const computeAge = (iso) => {
    if (!iso) return null;
    try {
      const b = new Date(iso);
      if (Number.isNaN(b.getTime())) return null;
      const now = new Date();
      let a = now.getFullYear() - b.getFullYear();
      const m = now.getMonth() - b.getMonth();
      if (m < 0 || (m === 0 && now.getDate() < b.getDate())) a -= 1;
      return a >= 0 && a < 150 ? a : null;
    } catch { return null; }
  };
  const driverAge = computeAge(d.birth_date);

  // Sezione comune mostrata SOLO in publicMode per tutti i tipi non-auto.
  // Replica i campi auto-style (storico polizza attuale + altri preventivi) ESCLUSE le garanzie/coperture attuali (merit_class, sinistri, classe propria, Bersani).
  const renderCommonPublicSections = () => (
    <>
      {/* === Storico polizza attuale === */}
      <div className="col-span-2 text-[11px] uppercase tracking-wide text-blue-700 font-semibold border-b border-blue-100 pb-1 mt-3">Storico polizza attuale</div>
      <div>
        <Label>Compagnia attuale</Label>
        <VehicleAutocomplete
          testid="qd-current-company"
          value={d.current_company || ""}
          onChange={(v) => set({ current_company: v })}
          suggestions={companyNames}
          placeholder="Es. UnipolSai"
        />
        <p className="text-[10px] text-slate-500 mt-0.5">Compagnia con cui sei attualmente assicurato (lascia vuoto se prima polizza)</p>
      </div>
      <div>
        <Label>Scadenza polizza attuale</Label>
        <DatePicker data-testid="qd-current-expiry" value={d.current_policy_expiry || ""} onChange={(e) => set({ current_policy_expiry: e.target.value })} />
        <p className="text-[10px] text-slate-500 mt-0.5">Data in cui scade la tua polizza in corso (se ce l&apos;hai)</p>
      </div>
      <div>
        <Label>Anni assicurato</Label>
        <Input data-testid="qd-years-insured" type="number" min="0" max="50" value={d.years_insured || ""} onChange={(e) => set({ years_insured: e.target.value })} placeholder="Es. 5" />
        <p className="text-[10px] text-slate-500 mt-0.5">Anni totali in cui sei stato coperto da una polizza simile</p>
      </div>

      {/* === Altri preventivi (obbligatorio) === */}
      <div className="col-span-2 text-[11px] uppercase tracking-wide text-blue-700 font-semibold border-b border-blue-100 pb-1 mt-3">Altri preventivi (obbligatorio)</div>
      <div className="col-span-2">
        <Label>Ha già richiesto altri preventivi? <span className="text-red-500">*</span></Label>
        <Select
          value={d.other_quotes_done === true ? "yes" : d.other_quotes_done === false ? "no" : ""}
          onValueChange={(v) => set({
            other_quotes_done: v === "yes",
            other_quotes_amount: v === "yes" ? d.other_quotes_amount || "" : "",
            other_quotes_frazionamento: v === "yes" ? d.other_quotes_frazionamento || "" : "",
          })}
        >
          <SelectTrigger data-testid="qd-other-quotes-done"><SelectValue placeholder="Seleziona…" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="no">No</SelectItem>
            <SelectItem value="yes">Sì</SelectItem>
          </SelectContent>
        </Select>
      </div>
      {d.other_quotes_done === true && (
        <>
          <div>
            <Label>Importo miglior preventivo (€) <span className="text-red-500">*</span></Label>
            <Input data-testid="qd-other-quotes-amount" type="number" step="0.01" min="0" value={d.other_quotes_amount || ""} onChange={(e) => set({ other_quotes_amount: e.target.value })} placeholder="Es. 450" required className="bg-yellow-50" />
          </div>
          <div>
            <Label>Frazionamento offerto <span className="text-red-500">*</span></Label>
            <Select value={d.other_quotes_frazionamento || ""} onValueChange={(v) => set({ other_quotes_frazionamento: v })}>
              <SelectTrigger data-testid="qd-other-quotes-frazionamento" className="bg-yellow-50"><SelectValue placeholder="Seleziona…" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="annuale">Annuale</SelectItem>
                <SelectItem value="semestrale">Semestrale</SelectItem>
                <SelectItem value="quadrimestrale">Quadrimestrale</SelectItem>
                <SelectItem value="trimestrale">Trimestrale</SelectItem>
                <SelectItem value="mensile">Mensile</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </>
      )}
    </>
  );

  // 🆕 Helper: label leggibile per ogni campo veicolo
  const VEHICLE_FIELD_LABELS = {
    vehicle_plate: "Targa",
    vehicle_brand: "Marca",
    vehicle_model: "Modello",
    vehicle_first_registration: "Data prima immatricolazione",
    vehicle_fuel: "Alimentazione",
    vehicle_kw: "Potenza (kW)",
    vehicle_displacement: "Cilindrata (cc)",
    chassis_number: "Numero telaio",
    vehicle_purchase_date: "Data acquisto veicolo",
    vehicle_seats: "Numero posti",
    vehicle_color: "Colore",
  };

  // 🆕 Sezione dinamica costruita dalla config policy_type (vehicle_fields_selected) + policy_products.
  // Rendering per qualsiasi tipologia (anche custom) che non sia coperta dai branch hardcoded sotto.
  const renderDynamicTypeSection = () => {
    const enabledFields = policyTypeConfig?.vehicle_fields_enabled
      ? (policyTypeConfig?.vehicle_fields_selected || [])
      : [];
    return (
      <div className="grid grid-cols-2 gap-3" data-testid={`qd-dynamic-${type}`}>
        {/* === Campi veicolo dinamici === */}
        {enabledFields.length > 0 && (
          <>
            <div className="col-span-2 text-[11px] uppercase tracking-wide text-sky-700 font-semibold border-b border-sky-100 pb-1 mt-1">
              Dati veicolo
            </div>
            {enabledFields.map((f) => {
              const isDate = f.endsWith("_date") || f === "vehicle_first_registration";
              const isNumber = f === "vehicle_kw" || f === "vehicle_displacement" || f === "vehicle_seats";
              return (
                <div key={f} className={f === "chassis_number" ? "col-span-2" : ""}>
                  <Label>{VEHICLE_FIELD_LABELS[f] || f}</Label>
                  <Input
                    data-testid={`qd-dyn-${f}`}
                    type={isDate ? "date" : isNumber ? "number" : "text"}
                    value={d[f] || ""}
                    onChange={(e) => set({ [f]: e.target.value })}
                    className={f === "vehicle_plate" ? "uppercase font-mono" : ""}
                  />
                </div>
              );
            })}
          </>
        )}
        {/* === Garanzie / Prodotti disponibili === */}
        {policyProducts.length > 0 && (
          <>
            <div className="col-span-2 text-[11px] uppercase tracking-wide text-amber-700 font-semibold border-b border-amber-100 pb-1 mt-2">
              Garanzie disponibili
            </div>
            <div className="col-span-2 grid grid-cols-1 sm:grid-cols-2 gap-1.5">
              {policyProducts.map((p) => {
                const sel = Array.isArray(d.selected_products) ? d.selected_products : [];
                const checked = sel.includes(p.code);
                return (
                  <label key={p.code} className="flex items-center gap-2 text-xs px-2 py-1.5 rounded border border-slate-200 hover:bg-amber-50 cursor-pointer">
                    <input
                      type="checkbox"
                      data-testid={`qd-prod-${p.code}`}
                      checked={checked}
                      onChange={(e) => {
                        const cur = new Set(sel);
                        if (e.target.checked) cur.add(p.code); else cur.delete(p.code);
                        set({ selected_products: [...cur] });
                      }}
                      className="h-4 w-4"
                    />
                    <span className="flex-1 min-w-0">
                      <span className="font-semibold">{p.label}</span>
                      {p.description && <span className="block text-[10px] text-slate-500 truncate">{p.description}</span>}
                    </span>
                  </label>
                );
              })}
            </div>
          </>
        )}
        {/* === Fallback se nessuna config: solo note === */}
        {enabledFields.length === 0 && policyProducts.length === 0 && (
          <div className="col-span-2">
            <p className="text-xs text-slate-500 italic">
              Nessun dato specifico configurato per la tipologia <strong>{policyTypeConfig?.label || type}</strong>.
              Per personalizzare, vai a <em>Gestione Agenzia → Tipologie polizze</em> e/o <em>Liste preventivo → Prodotti per tipologia</em>.
            </p>
          </div>
        )}
        {publicMode && renderCommonPublicSections()}
      </div>
    );
  };

  if (type === "auto" || type === "moto") {
    return (
      <div className="grid grid-cols-2 gap-3" data-testid="qd-auto">
        {/* === Veicolo (collassabile) === */}
        <button
          type="button"
          onClick={() => setVehicleCollapsed((v) => !v)}
          data-testid="qd-section-veicolo-toggle"
          className="col-span-2 mt-1 px-3 py-2 rounded-md bg-sky-100 border-l-4 border-sky-600 text-[12px] uppercase tracking-wide text-sky-900 font-bold flex items-center justify-between gap-2 hover:bg-sky-200 transition-colors text-left"
          title={vehicleCollapsed ? "Espandi dati veicolo" : "Comprimi dati veicolo"}
        >
          <span className="flex items-center gap-2">🚗 Dati Veicolo</span>
          {vehicleCollapsed ? <ChevronDown className="h-4 w-4" /> : <ChevronUp className="h-4 w-4" />}
        </button>
        {!vehicleCollapsed && (<>
        <div className="col-span-2">
          <Label>Targa {requirePlate && <span className="text-red-500">*</span>}</Label>
          <div className="flex gap-2 items-start">
            <div className="flex-1">
              <PlateInput
                testid="qd-plate"
                value={d.vehicle_plate || ""}
                onChange={(v) => set({ vehicle_plate: v })}
                overrideFlag={!!d.vehicle_plate_override}
                onOverrideChange={(v) => set({ vehicle_plate_override: v })}
                className="text-lg"
              />
            </div>
            <CopyButton testid="qd-plate-copy" label="Targa" value={d.vehicle_plate} />
          </div>
          {requirePlate && <p className="text-[10px] text-rose-600 mt-0.5 italic">⚠️ campo obbligatorio</p>}
        </div>
        <div>
          <Label>Data 1ª Immatricolazione {publicMode && <span className="text-red-500">*</span>}</Label>
          <DatePicker
            data-testid="qd-year"
            max={new Date().toISOString().slice(0,10)}
            min="1900-01-01"
            value={(() => {
              const v = d.vehicle_year || "";
              // Retro-compat: valori legacy "MM/YYYY" o "YYYY" → converte in ISO YYYY-MM-01 / YYYY-01-01
              if (/^\d{2}\/\d{4}$/.test(v)) {
                const [mm, yy] = v.split("/");
                return `${yy}-${mm}-01`;
              }
              if (/^\d{4}$/.test(v)) return `${v}-01-01`;
              if (/^\d{4}-\d{2}-\d{2}$/.test(v)) return v;
              return "";
            })()}
            onChange={(e) => {
              const v = e.target.value;
              if (v) {
                const todayIso = new Date();
                todayIso.setMinutes(todayIso.getMinutes() - todayIso.getTimezoneOffset());
                const todayStr = todayIso.toISOString().slice(0, 10);
                if (v > todayStr) {
                  if (typeof window !== "undefined") window.alert("La data di 1ª immatricolazione non può essere nel futuro.");
                  return;
                }
              }
              set({ vehicle_year: v });
            }}
          />
          <p className="text-[10px] text-slate-500 mt-0.5">Data completa della prima immatricolazione</p>
        </div>
        <div>
          <Label>Data acquisto veicolo <span className="text-red-500">*</span></Label>
          <DatePicker data-testid="qd-purchase-date" max={new Date().toISOString().slice(0,10)}
            min="1900-01-01"
            value={d.vehicle_purchase_date || ""}
            onChange={(e) => {
              const v = e.target.value;
              if (v) {
                // 🆕 Confronto come stringa YYYY-MM-DD (evita bug di fuso orario che scartava la data odierna)
                const todayIso = new Date();
                todayIso.setMinutes(todayIso.getMinutes() - todayIso.getTimezoneOffset());
                const todayStr = todayIso.toISOString().slice(0, 10);
                if (v > todayStr) {
                  if (typeof window !== "undefined") window.alert("La data di acquisto non può essere nel futuro.");
                  return;
                }
              }
              set({ vehicle_purchase_date: v });
            }} />
          <p className="text-[10px] text-slate-500 mt-0.5">Data del passaggio di proprietà / acquisto</p>
        </div>
        <div>
          <Label>Marca</Label>
          <VehicleAutocomplete
            testid="qd-brand"
            value={d.vehicle_brand || ""}
            onChange={(v) => set({ vehicle_brand: v })}
            suggestions={brandSuggestions}
            placeholder="Es. Fiat, Volkswagen…"
          />
          <p className="text-[10px] text-slate-500 mt-0.5">Casa costruttrice del veicolo</p>
        </div>
        <div>
          <Label>Modello</Label>
          <VehicleAutocomplete
            testid="qd-model"
            value={d.vehicle_model || ""}
            onChange={(v) => set({ vehicle_model: v })}
            suggestions={modelSuggestions}
            placeholder={currentBrand ? "Es. Panda, Golf…" : "Inserisci prima la marca"}
          />
          <p className="text-[10px] text-slate-500 mt-0.5">Modello specifico (es. Panda, Golf)</p>
        </div>
        <div>
          <Label>Alimentazione</Label>
          <Select
            value={d.vehicle_fuel || d.fuel || ""}
            onValueChange={(v) => set({ vehicle_fuel: v, fuel: v })}
          >
            <SelectTrigger data-testid="qd-fuel"><SelectValue placeholder="Seleziona…" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="benzina">Benzina</SelectItem>
              <SelectItem value="diesel">Diesel</SelectItem>
              <SelectItem value="gpl">GPL</SelectItem>
              <SelectItem value="metano">Metano</SelectItem>
              <SelectItem value="elettrico">Elettrico</SelectItem>
              <SelectItem value="ibrido">Ibrido</SelectItem>
            </SelectContent>
          </Select>
          <p className="text-[10px] text-slate-500 mt-0.5">Tipo di carburante usato dal veicolo</p>
        </div>
        <div><Label>Cilindrata (cc)</Label><Input data-testid="qd-displacement" type="number" min="0" value={d.vehicle_displacement || d.displacement || ""} onChange={(e) => set({ vehicle_displacement: e.target.value, displacement: e.target.value })} placeholder="Es. 1200" /><p className="text-[10px] text-slate-500 mt-0.5">Cilindrata in cc - vedi libretto sezione P.1</p></div>
        <div><Label>Potenza (KW)</Label><Input data-testid="qd-kw" type="number" min="0" value={d.vehicle_kw || d.kw || ""} onChange={(e) => set({ vehicle_kw: e.target.value, kw: e.target.value })} placeholder="Es. 70" /><p className="text-[10px] text-slate-500 mt-0.5">Potenza in kilowatt - vedi libretto sezione P.2</p></div>
        <div>
          <Label>Km annui</Label>
          {(publicAnnualMileage && publicAnnualMileage.length > 0) ? (
            <Select value={d.annual_km || ""} onValueChange={(v) => set({ annual_km: v })}>
              <SelectTrigger data-testid="qd-km"><SelectValue placeholder="Seleziona…" /></SelectTrigger>
              <SelectContent>
                {publicAnnualMileage.map((o) => (
                  <SelectItem key={o.code} value={o.code}>{o.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          ) : (
            <Input data-testid="qd-km" type="number" value={d.annual_km || ""} onChange={(e) => set({ annual_km: e.target.value })} placeholder="Es. 15000" />
          )}
          <p className="text-[10px] text-slate-500 mt-0.5">Chilometri stimati che percorri in un anno</p>
        </div>
        <div>
          <Label>Uso del veicolo</Label>
          <Select value={d.use || ""} onValueChange={(v) => set({ use: v })}>
            <SelectTrigger data-testid="qd-use"><SelectValue placeholder="Seleziona…" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="privato">Privato</SelectItem>
              <SelectItem value="professionale">Professionale</SelectItem>
              <SelectItem value="autonoleggio">Autonoleggio</SelectItem>
            </SelectContent>
          </Select>
          <p className="text-[10px] text-slate-500 mt-0.5">Privato = uso personale; Professionale = uso lavoro</p>
        </div>
        <div>
          <Label>Custodia</Label>
          <Select value={d.parking || ""} onValueChange={(v) => set({ parking: v })}>
            <SelectTrigger data-testid="qd-parking"><SelectValue placeholder="Seleziona…" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="box">Box / Garage</SelectItem>
              <SelectItem value="posto_auto_coperto">Posto auto coperto</SelectItem>
              <SelectItem value="strada">Strada / All&apos;aperto</SelectItem>
              <SelectItem value="cortile">Cortile privato</SelectItem>
            </SelectContent>
          </Select>
          <p className="text-[10px] text-slate-500 mt-0.5">Dove parcheggi abitualmente il veicolo di notte</p>
        </div>
        <div>
          <Label>Antifurto</Label>
          <Select value={d.anti_theft || ""} onValueChange={(v) => set({ anti_theft: v })}>
            <SelectTrigger data-testid="qd-antitheft"><SelectValue placeholder="Seleziona…" /></SelectTrigger>
            <SelectContent>
              {(publicAntitheft && publicAntitheft.length > 0) ? (
                publicAntitheft.map((o) => (
                  <SelectItem key={o.code} value={o.code}>{o.label}</SelectItem>
                ))
              ) : (<>
                <SelectItem value="nessuno">Nessuno</SelectItem>
                <SelectItem value="immobilizer">Immobilizer di serie</SelectItem>
                <SelectItem value="allarme">Allarme</SelectItem>
                <SelectItem value="satellitare">Satellitare</SelectItem>
              </>)}
            </SelectContent>
          </Select>
          <p className="text-[10px] text-slate-500 mt-0.5">Sistema di sicurezza installato sul veicolo</p>
        </div>
        </>)}

        {/* === Conducente (collassabile) === */}
        <button
          type="button"
          onClick={() => setDriverCollapsed((v) => !v)}
          data-testid="qd-section-conducente-toggle"
          className="col-span-2 mt-3 px-3 py-2 rounded-md bg-amber-100 border-l-4 border-amber-600 text-[12px] uppercase tracking-wide text-amber-900 font-bold flex items-center justify-between gap-2 hover:bg-amber-200 transition-colors text-left"
          title={driverCollapsed ? "Espandi dati conducente" : "Comprimi dati conducente"}
        >
          <span className="flex items-center gap-2">👤 Dati Conducente</span>
          {driverCollapsed ? <ChevronDown className="h-4 w-4" /> : <ChevronUp className="h-4 w-4" />}
        </button>
        {!driverCollapsed && (<>
        <div><Label>Data nascita conducente {driverAge !== null && <span className="text-xs font-semibold text-blue-700 ml-1.5" data-testid="qd-age">({driverAge} anni)</span>}</Label><DatePicker data-testid="qd-birth-date" value={d.birth_date || ""} onChange={(e) => set({ birth_date: e.target.value })} /><p className="text-[10px] text-slate-500 mt-0.5">Data di nascita del conducente principale</p></div>
        <div><Label>Conducente più giovane (età)</Label><Input data-testid="qd-youngest-age" type="number" min="14" max="99" value={d.youngest_driver_age || ""} onChange={(e) => set({ youngest_driver_age: e.target.value })} placeholder="Es. 22" /><p className="text-[10px] text-slate-500 mt-0.5">Età del guidatore più giovane che userà il veicolo</p></div>
        <div className="col-span-2">
          <Label>Data di primo rilascio patente <span className="text-[10px] text-slate-500 font-normal">(vedi dietro la patente)</span></Label>
          <DatePicker data-testid="qd-license-first-issue" value={d.license_first_issue || ""}
            onChange={(e) => set({ license_first_issue: e.target.value })} />
          <p className="text-[11px] text-slate-500 mt-1">📋 Sul retro della patente, casella 10 (data di rilascio della categoria B).</p>
        </div>
        <div>
          <Label>Tipo di guida</Label>
          <Select value={d.driving_type || ""} onValueChange={(v) => set({ driving_type: v, driving_type_manual: true })}>
            <SelectTrigger data-testid="qd-driving-type"><SelectValue placeholder="Seleziona…" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="esclusiva">Esclusiva</SelectItem>
              <SelectItem value="esperta">Esperta</SelectItem>
              <SelectItem value="libera">Libera</SelectItem>
            </SelectContent>
          </Select>
          {d.driving_type && !d.driving_type_manual && (
            <p className="text-[10px] text-blue-600 mt-1">ⓘ Suggerito automaticamente in base all&apos;età del conducente più giovane.</p>
          )}
          {showDrivingTypeWarning && type === "auto" && !d.driving_type && (
            <p className="text-[11px] text-red-600 font-medium mt-1" data-testid="qd-driving-type-warning">⚠ Campo obbligatorio per inviare il preventivo ai partner</p>
          )}
        </div>
        <div>
          <Label>Titolo di studio</Label>
          <Select value={d.education || ""} onValueChange={(v) => set({ education: v })}>
            <SelectTrigger data-testid="qd-education"><SelectValue placeholder="Seleziona…" /></SelectTrigger>
            <SelectContent>
              {(publicEducation && publicEducation.length > 0) ? (
                publicEducation.map((o) => (
                  <SelectItem key={o.code} value={o.code}>{o.label}</SelectItem>
                ))
              ) : (<>
                <SelectItem value="licenza_media">Licenza media</SelectItem>
                <SelectItem value="diploma">Diploma</SelectItem>
                <SelectItem value="laurea_breve">Laurea triennale</SelectItem>
                <SelectItem value="laurea_magistrale">Laurea magistrale</SelectItem>
                <SelectItem value="master_dottorato">Master / Dottorato</SelectItem>
              </>)}
            </SelectContent>
          </Select>
        </div>
        <div className="col-span-2"><Label>Professione</Label>
          <VehicleAutocomplete
            testid="qd-profession"
            value={d.profession || ""}
            onChange={(v) => set({ profession: v })}
            suggestions={professions}
            placeholder="Es. Impiegato, Insegnante, Libero professionista..."
          />
        </div>
        </>)}

        {/* === Storico assicurativo (collassabile) ===
        {/* Sezione Attestato di rischio ATTUALE: nascosta se "Nuovo veicolo da assicurare" (dato che l'auto non ha ancora una polizza precedente). */}
        {d.insurance_status !== "new_vehicle" && (<>
        <button
          type="button"
          onClick={() => setHistoryCollapsed((v) => !v)}
          data-testid="qd-section-storico-toggle"
          className="col-span-2 mt-3 px-3 py-2 rounded-md bg-violet-100 border-l-4 border-violet-600 text-[12px] uppercase tracking-wide text-violet-900 font-bold flex items-center justify-between gap-2 hover:bg-violet-200 transition-colors text-left"
          title={historyCollapsed ? "Espandi storico assicurativo" : "Comprimi storico assicurativo"}
        >
          <span className="flex items-center gap-2">📜 Storico Assicurativo</span>
          {historyCollapsed ? <ChevronDown className="h-4 w-4" /> : <ChevronUp className="h-4 w-4" />}
        </button>
        {!historyCollapsed && (<>
        <div><Label>Classe di merito ATTUALE</Label><Input data-testid="qd-merit" type="number" min="1" max="18" value={d.merit_class || ""} onChange={(e) => set({ merit_class: e.target.value })} placeholder="1-18" /><p className="text-[10px] text-slate-500 mt-0.5">Da 1 (la migliore) a 18 - vedi attestato di rischio</p></div>
        <div><Label>Sinistri ultimi 5 anni</Label><Input data-testid="qd-claims-5y" type="number" min="0" value={d.claims_last_5y || ""} onChange={(e) => set({ claims_last_5y: e.target.value })} placeholder="Es. 0" /><p className="text-[10px] text-slate-500 mt-0.5">Quanti sinistri con tua responsabilità negli ultimi 5 anni</p></div>
        <div><Label>Compagnia attuale</Label>
          {(publicCurrentCompanies && publicCurrentCompanies.length > 0) ? (
            <Select value={d.current_company || ""} onValueChange={(v) => set({ current_company: v })}>
              <SelectTrigger data-testid="qd-current-company"><SelectValue placeholder="Seleziona compagnia…" /></SelectTrigger>
              <SelectContent>
                {publicCurrentCompanies.map((o) => (
                  <SelectItem key={o.code} value={o.code}>{o.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          ) : (
            <VehicleAutocomplete
              testid="qd-current-company"
              value={d.current_company || ""}
              onChange={(v) => set({ current_company: v })}
              suggestions={companyNames}
              placeholder="Es. UnipolSai"
            />
          )}
          <p className="text-[10px] text-slate-500 mt-0.5">Compagnia con cui sei attualmente assicurato</p>
        </div>
        <div><Label>Scadenza polizza attuale</Label><DatePicker data-testid="qd-current-expiry" value={d.current_policy_expiry || ""} onChange={(e) => set({ current_policy_expiry: e.target.value })} /><p className="text-[10px] text-slate-500 mt-0.5">Data in cui scade la tua polizza in corso</p></div>
        </>)}
        </>)}

        {/* 🆕 Feb 2026 — Sezione "Attestato di rischio di un altro veicolo" rimossa perché
            duplicativa rispetto alla sezione "Agevolazione: Legge Bersani o RC Familiare"
            presente in `PolicyWizard.jsx`. Anche la sotto-sezione "Bersani propria" è stata
            rimossa: la nuova Bersani section del wizard copre entrambi gli scenari (propria vs.
            parentela) via il campo `bersani_type` (legge_bersani | rc_familiare). */}

        {/* === Altri preventivi === */}
        <div className="col-span-2 text-[11px] uppercase tracking-wide text-blue-700 font-semibold border-b border-blue-100 pb-1 mt-3">Altri preventivi (obbligatorio)</div>
        <div className="col-span-2">
          <Label>Ha già richiesto altri preventivi? <span className="text-red-500">*</span></Label>
          <Select
            value={d.other_quotes_done === true ? "yes" : d.other_quotes_done === false ? "no" : ""}
            onValueChange={(v) => set({
              other_quotes_done: v === "yes",
              other_quotes_amount: v === "yes" ? d.other_quotes_amount || "" : "",
              other_quotes_frazionamento: v === "yes" ? d.other_quotes_frazionamento || "" : "",
            })}
          >
            <SelectTrigger data-testid="qd-other-quotes-done"><SelectValue placeholder="Seleziona…" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="no">No</SelectItem>
              <SelectItem value="yes">Sì</SelectItem>
            </SelectContent>
          </Select>
        </div>
        {d.other_quotes_done === true && (
          <>
            <div>
              <Label>Importo miglior preventivo (€) <span className="text-red-500">*</span></Label>
              <Input
                data-testid="qd-other-quotes-amount"
                type="number"
                step="0.01"
                min="0"
                value={d.other_quotes_amount || ""}
                onChange={(e) => set({ other_quotes_amount: e.target.value })}
                placeholder="Es. 450"
                required
                className="bg-yellow-50"
              />
            </div>
            <div>
              <Label>Frazionamento offerto <span className="text-red-500">*</span></Label>
              <Select value={d.other_quotes_frazionamento || ""} onValueChange={(v) => set({ other_quotes_frazionamento: v })}>
                <SelectTrigger data-testid="qd-other-quotes-frazionamento" className="bg-yellow-50"><SelectValue placeholder="Seleziona…" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="annuale">Annuale</SelectItem>
                  <SelectItem value="semestrale">Semestrale</SelectItem>
                  <SelectItem value="quadrimestrale">Quadrimestrale</SelectItem>
                  <SelectItem value="trimestrale">Trimestrale</SelectItem>
                  <SelectItem value="mensile">Mensile</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </>
        )}
      </div>
    );
  }

  if (type === "casa") {
    const opts = ["furto", "incendio", "rc_capofamiglia", "danni_acqua", "eventi_atmosferici", "tutela_legale"];
    return (
      <div className="grid grid-cols-2 gap-3" data-testid="qd-casa">
        <div>
          <Label>Tipo immobile</Label>
          <Select value={d.property_type || ""} onValueChange={(v) => set({ property_type: v })}>
            <SelectTrigger data-testid="qd-ptype"><SelectValue placeholder="Seleziona…" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="appartamento">Appartamento</SelectItem>
              <SelectItem value="villa">Villa/Casa indipendente</SelectItem>
              <SelectItem value="bifamiliare">Bi/Trifamiliare</SelectItem>
              <SelectItem value="commerciale">Locale commerciale</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div><Label>Superficie (m²)</Label><Input data-testid="qd-sqm" type="number" value={d.property_sqm || ""} onChange={(e) => set({ property_sqm: e.target.value })} /></div>
        <div className="col-span-2"><Label>Indirizzo immobile</Label><Input data-testid="qd-paddr" value={d.property_address || ""} onChange={(e) => set({ property_address: e.target.value })} placeholder="Via, n°, città" /></div>
        <div><Label>Anno costruzione</Label><Input data-testid="qd-pyear" type="number" min="1800" max="2030" value={d.property_year || ""} onChange={(e) => set({ property_year: e.target.value })} /></div>
        <div><Label>Valore stimato (€)</Label><MoneyInput data-testid="qd-pvalue" value={d.property_value || ""} onChange={(v) => set({ property_value: v })} /></div>
        <div className="col-span-2">
          <Label>Garanzie richieste</Label>
          <div className="grid grid-cols-2 gap-2 mt-2 p-3 rounded-md bg-slate-50 border border-slate-200">
            {opts.map((o) => {
              const checked = (d.coverage_options || []).includes(o);
              return (
                <label key={o} data-testid={`qd-cov-${o}`} className="flex items-center gap-2 text-sm cursor-pointer">
                  <Checkbox checked={checked} onCheckedChange={() => toggleCoverage(o)} />
                  <span className="capitalize">{o.replace(/_/g, " ")}</span>
                </label>
              );
            })}
          </div>
        </div>
        {publicMode && renderCommonPublicSections()}
      </div>
    );
  }

  if (type === "vita") {
    return (
      <div className="grid grid-cols-2 gap-3" data-testid="qd-vita">
        <div><Label>Data di nascita</Label><DatePicker data-testid="qd-bdate" value={d.birth_date || ""} onChange={(e) => set({ birth_date: e.target.value })} /></div>
        <div>
          <Label>Fumatore</Label>
          <Select value={d.smoker || ""} onValueChange={(v) => set({ smoker: v })}>
            <SelectTrigger data-testid="qd-smoker"><SelectValue placeholder="Seleziona…" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="no">No</SelectItem>
              <SelectItem value="si">Sì</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div><Label>Capitale assicurato (€)</Label><MoneyInput data-testid="qd-capital" value={d.insured_capital || ""} onChange={(v) => set({ insured_capital: v })} placeholder="Es. 100.000,00" /></div>
        <div><Label>Durata (anni)</Label><Input data-testid="qd-duration" type="number" min="1" max="50" value={d.duration_years || ""} onChange={(e) => set({ duration_years: e.target.value })} /></div>
        <div className="col-span-2"><Label>Beneficiari</Label><Input data-testid="qd-benef" value={d.beneficiaries || ""} onChange={(e) => set({ beneficiaries: e.target.value })} placeholder="Es. Coniuge e figli" /></div>
        {publicMode && renderCommonPublicSections()}
      </div>
    );
  }

  if (type === "salute") {
    return (
      <div className="grid grid-cols-2 gap-3" data-testid="qd-salute">
        <div><Label>Data di nascita</Label><DatePicker data-testid="qd-bdate" value={d.birth_date || ""} onChange={(e) => set({ birth_date: e.target.value })} /></div>
        <div><Label>Componenti nucleo</Label><Input data-testid="qd-family" type="number" min="1" max="10" value={d.family_members || ""} onChange={(e) => set({ family_members: e.target.value })} /></div>
        <div className="col-span-2"><Label>Patologie preesistenti</Label><Input data-testid="qd-preex" value={d.preexisting_conditions || ""} onChange={(e) => set({ preexisting_conditions: e.target.value })} placeholder="Indica eventuali patologie (facoltativo)" /></div>
        {publicMode && renderCommonPublicSections()}
      </div>
    );
  }

  if (type === "infortuni") {
    return (
      <div className="grid grid-cols-2 gap-3" data-testid="qd-infortuni">
        <div><Label>Professione</Label>
          <VehicleAutocomplete
            testid="qd-prof"
            value={d.profession || ""}
            onChange={(v) => set({ profession: v })}
            suggestions={professions}
          />
        </div>
        <div><Label>Attività principale</Label><Input data-testid="qd-activity" value={d.activity || ""} onChange={(e) => set({ activity: e.target.value })} placeholder="Sportiva/Manuale/Ufficio…" /></div>
        <div><Label>Capitale invalidità (€)</Label><MoneyInput data-testid="qd-inv" value={d.invalidity_capital || ""} onChange={(v) => set({ invalidity_capital: v })} /></div>
        <div><Label>Capitale caso morte (€)</Label><MoneyInput data-testid="qd-death" value={d.death_capital || ""} onChange={(v) => set({ death_capital: v })} /></div>
        {publicMode && renderCommonPublicSections()}
      </div>
    );
  }

  if (type === "viaggio") {
    return (
      <div className="grid grid-cols-2 gap-3" data-testid="qd-viaggio">
        <div className="col-span-2"><Label>Destinazione</Label><Input data-testid="qd-dest" value={d.destination || ""} onChange={(e) => set({ destination: e.target.value })} placeholder="Es. Spagna, USA…" /></div>
        <div><Label>Data partenza</Label><DatePicker data-testid="qd-dep" value={d.departure_date || ""} onChange={(e) => set({ departure_date: e.target.value })} /></div>
        <div><Label>Data ritorno</Label><DatePicker data-testid="qd-ret" value={d.return_date || ""} onChange={(e) => set({ return_date: e.target.value })} /></div>
        <div><Label>N° viaggiatori</Label><Input data-testid="qd-trav" type="number" min="1" max="20" value={d.travelers_count || ""} onChange={(e) => set({ travelers_count: e.target.value })} /></div>
        {publicMode && renderCommonPublicSections()}
      </div>
    );
  }

  if (type === "responsabilita_civile") {
    return (
      <div className="grid grid-cols-2 gap-3" data-testid="qd-rc">
        <div>
          <Label>Ambito</Label>
          <Select value={d.rc_scope || ""} onValueChange={(v) => set({ rc_scope: v })}>
            <SelectTrigger data-testid="qd-rcscope"><SelectValue placeholder="Seleziona…" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="capofamiglia">Capofamiglia</SelectItem>
              <SelectItem value="professionale">Professionale</SelectItem>
              <SelectItem value="animali">Animali domestici</SelectItem>
              <SelectItem value="altro">Altro</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div><Label>Massimale richiesto (€)</Label><MoneyInput data-testid="qd-max" value={d.rc_max_coverage || ""} onChange={(v) => set({ rc_max_coverage: v })} placeholder="Es. 1.000.000,00" /></div>
        {publicMode && renderCommonPublicSections()}
      </div>
    );
  }

  return renderDynamicTypeSection();
};
